/**
 * Producer picker page (AC3). Shown when an advisor has multiple
 * producer assignments and must choose which one to act as.
 * Redirected to by `apiFetch` on 409 PRODUCER_REQUIRED, or by the
 * callback's multi-match redirect.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Card, Container, Spinner } from 'react-bootstrap';
import { useAuth, type ProducerOption } from './AuthContext.js';
import { apiFetch } from '../lib/api-fetch.js';
import { relativeUsedAt } from './relative-time.js';
import { useReplaceCallbackHistory } from './use-replace-callback-history.js';

export function SelectProducerPage() {
    const { producerId, producerOptions, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useReplaceCallbackHistory('/auth/select-producer');

    // Already chosen — redirect to dashboard (handles back-button case).
    useEffect(() => {
        if (producerId) {
            navigate('/portal/advisor-dashboard', { replace: true });
        }
    }, [producerId, navigate]);

    if (producerId) return null;

    // Defence in depth — shouldn't be here without options.
    if (producerOptions.length === 0) {
        return (
            <Container className="py-5" style={{ maxWidth: 520 }}>
                <Alert variant="warning">
                    <Alert.Heading>Nothing to select</Alert.Heading>
                    <p className="mb-2">
                        Your account doesn&rsquo;t have any producer options available.
                        Try signing out and back in.
                    </p>
                    <button className="btn btn-outline-secondary" onClick={() => void logout()}>
                        Sign out
                    </button>
                </Alert>
            </Container>
        );
    }

    async function handleSelect(option: ProducerOption) {
        setLoading(option.id);
        setError(null);
        try {
            const res = await apiFetch('/api/me/producer', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ producerId: option.id }),
                skipAuthRedirect: true,
            });
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                setError(
                    (body as { message?: string } | null)?.message ??
                    'Something went wrong. Please try again.',
                );
                setLoading(null);
                return;
            }
            await refresh();
            navigate('/portal/advisor-dashboard', { replace: true });
        } catch {
            setError('Network error. Please check your connection and try again.');
            setLoading(null);
        }
    }

    return (
        <div className="d-flex align-items-center min-vh-100 bg-light py-4">
            <Container style={{ maxWidth: 520 }}>
                <h2 className="mb-1 text-center">Who are you working with today?</h2>
                <p className="text-secondary text-center mb-4">
                    You&rsquo;re associated with more than one organisation.
                    Choose the one you&rsquo;d like to act under for this session.
                </p>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <div className="d-grid gap-3">
                    {producerOptions.map((option, index) => (
                        <Card
                            key={option.id}
                            role="button"
                            tabIndex={0}
                            className={`border shadow-sm ${loading ? 'opacity-50' : ''}`}
                            style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                            onClick={() => !loading && handleSelect(option)}
                            onKeyDown={(e) => {
                                if (!loading && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    handleSelect(option);
                                }
                            }}
                        >
                            <Card.Body className="d-flex align-items-start justify-content-between">
                                <div>
                                    <div className="fw-semibold fs-5">
                                        {option.label}
                                        {index === 0 && option.lastUsedAt && (
                                            <Badge bg="primary" className="ms-2 align-middle">
                                                Last used
                                            </Badge>
                                        )}
                                    </div>
                                    {/* Show producer id under the agency label so testers/support can
                                        disambiguate two tiles that share an MGA company name and users
                                        can confirm which producer they're switching to. */}
                                    {option.id !== option.label && (
                                        <small className="text-muted d-block font-monospace">
                                            {option.id}
                                        </small>
                                    )}
                                    {option.lastUsedAt && (
                                        <small className="text-muted">
                                            {relativeUsedAt(option.lastUsedAt)}
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {!option.lastUsedAt && (
                                        <small className="text-muted fst-italic opacity-50">
                                            No activity yet
                                        </small>
                                    )}
                                    {loading === option.id && (
                                        <Spinner animation="border" size="sm" />
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            </Container>
        </div>
    );
}
